/**
 * RealmContext - Globaler State fuer GoodLife vs GoodWork Modus
 *
 * Verwaltet den aktiven Realm und persistiert die Auswahl im localStorage.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { Realm } from '../types';

const STORAGE_KEY = 'goodplace-realm';
const DEFAULT_REALM: Realm = 'goodlife';

interface RealmContextType {
  realm: Realm;
  setRealm: (realm: Realm) => void;
  toggleRealm: () => void;
  isGoodWork: boolean;
  isGoodLife: boolean;
}

const RealmContext = createContext<RealmContextType>({
  realm: DEFAULT_REALM,
  setRealm: () => {},
  toggleRealm: () => {},
  isGoodWork: false,
  isGoodLife: true,
});

export function useRealm() {
  const context = useContext(RealmContext);
  if (!context) {
    throw new Error('useRealm must be used within a RealmProvider');
  }
  return context;
}

interface RealmProviderProps {
  children: React.ReactNode;
  initialRealm?: Realm;
}

export function RealmProvider({ children, initialRealm }: RealmProviderProps) {
  const [realm, setRealmState] = useState<Realm>(() => {
    // Prioritaet: 1. initialRealm Prop, 2. localStorage, 3. Default
    if (initialRealm) return initialRealm;

    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'goodlife' || stored === 'goodwork') {
        return stored;
      }
    }
    return DEFAULT_REALM;
  });

  // Persistiere Aenderungen im localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, realm);
    }
  }, [realm]);

  const setRealm = useCallback((newRealm: Realm) => {
    setRealmState(newRealm);
  }, []);

  const toggleRealm = useCallback(() => {
    setRealmState((current) => (current === 'goodlife' ? 'goodwork' : 'goodlife'));
  }, []);

  const value: RealmContextType = {
    realm,
    setRealm,
    toggleRealm,
    isGoodWork: realm === 'goodwork',
    isGoodLife: realm === 'goodlife',
  };

  return <RealmContext.Provider value={value}>{children}</RealmContext.Provider>;
}

export default RealmContext;
